/**
 * mcpCall — jednotné volání MCP toolu z dashboardu.
 *
 * Browser nezná MCP URL ani interní port. Posílá jen tool + arguments na
 * same-origin UI command capability; UI server volá lokální Python proxy na
 * loopbacku a ta znovu rozřeší entitu, credential, ACL i routing.
 *
 * Loading UX: >800ms loading toast; silent: `{ silent: true }`.
 */
import i18n from './i18n';
import { type OperationFeedbackOptions, withOperationFeedback } from './operation-feedback';
import {
  isAccessDeniedReasonCode,
  isAuthenticationReasonCode,
  triggerRelogin,
  triggerReloginForResponse,
} from './auth-relogin.ts';
import { setCallTarget } from './call-target.ts';
import { isUiGatewayResponse, reportUiTransportSample } from './ui-availability.ts';

// MCP-only přepínání nodu je odložené — zbytky v session by shodily levé menu.
try {
  setCallTarget(null);
} catch {
  /* ignore */
}

interface McpErrorData {
  reason_code?: string;
  phase?: string;
  detail?: string;
  remediation?: string;
}

export class McpClientError extends Error {
  constructor(
    message: string,
    readonly reasonCode: string,
    readonly phase: string,
    readonly remediation?: string,
  ) {
    super(message);
    this.name = 'McpClientError';
  }
}

function proxyError(message: string, data?: McpErrorData): McpClientError {
  const reasonCode = data?.reason_code ?? 'mcp_request_failed';
  const phase = data?.phase ?? 'proxy';
  const explanation = data?.remediation ? `${message} — ${data.remediation}` : message;
  return new McpClientError(explanation, reasonCode, phase, data?.remediation);
}

export type McpCallOptions = OperationFeedbackOptions & {
  /** Nechat volání na hostitelském nodu (inventář přepínače). */
  hostLocal?: boolean;
};

export async function mcpCall<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
  opts: McpCallOptions = {},
): Promise<T> {
  return withOperationFeedback(
    async () => {
      const transportStarted = performance.now();
      let r: Response;
      try {
        // Presentation boundary exception: tohle JE transportní vrstva (viz
        // hlavička souboru). Same-origin, fixní relativní cesta — žádná
        // sestavovaná URL, žádný port, žádný jiný fetch cíl smí v UI existovat.
        r = await fetch('/ui/commands/tool', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tool: name,
            arguments: args,
          }),
        });
      } catch {
        reportUiTransportSample({ ok: false, latencyMs: performance.now() - transportStarted });
        throw new McpClientError(
          i18n.t('common.mcpUnreachable', 'Lokální MCP proxy tohoto ACM nodu není dosažitelná.'),
          'local_mcp_unreachable',
          'local_proxy_transport',
          i18n.t('common.mcpUnreachableHint', 'Ověř službu acm-mcp na tomto nodu.'),
        );
      }
      // Délka MCP operace není síťová latence (tool může legitimně běžet
      // minuty). Z běžného volání proto hlásíme jen skutečnou ingress chybu;
      // RTT měří levný availability endpoint v guardu.
      if (isUiGatewayResponse(r)) {
        reportUiTransportSample({
          ok: false,
          gateway: true,
          latencyMs: performance.now() - transportStarted,
        });
      }
      if (triggerReloginForResponse(r)) {
        throw new McpClientError(
          i18n.t('common.sessionExpired', 'Přihlášení vypršelo; obnovuji autorizaci.'),
          'ui_session_expired',
          'ui_ingress',
          i18n.t(
            'common.sessionExpiredHint',
            'Po obnovení autorizace budete vráceni na tuto stránku.',
          ),
        );
      }
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: r.statusText })) as {
          error?: string | { message?: string };
          reason_code?: string;
          phase?: string;
          detail?: string;
          remediation?: string;
        };
        const msg = typeof err.error === 'string'
          ? err.error
          : err.error?.message ?? `MCP HTTP ${r.status}`;
        throw proxyError(msg, err);
      }
      const d = await r.json() as {
        result?: { content?: Array<{ text?: string }> };
        error?: { message?: string; data?: McpErrorData };
      };
      if (d.error) {
        const reason = d.error.data?.reason_code;
        // Bez oprávnění se NEpřihlašuje znovu — session je v pořádku, chybí
        // role. Relogin by vedl na login a ten zpět sem (smyčka).
        if (!isAccessDeniedReasonCode(reason) && isAuthenticationReasonCode(reason)) {
          triggerRelogin(reason);
        }
        throw proxyError(d.error.message ?? `MCP HTTP ${r.status}`, d.error.data);
      }
      const payload = d.result?.content?.[0]?.text;
      if (payload === undefined) throw new Error('Invalid MCP tool response');
      return JSON.parse(payload) as T;
    },
    name,
    { label: opts.label, silent: opts.silent },
  );
}
